import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  getConsent,
  setConsent,
  isProductionHost,
  isConsentRequiredRegion,
  isGpcSignaled,
  onConsentOpenRequest,
} from "@/lib/consent";

/**
 * Compact, dismissible bottom-left consent banner.
 * Renders only on production hosts. The first-visit prompt is gated on
 * region (EU/EEA/UK/CH) and Global Privacy Control. The footer
 * "Cookie preferences" link can always re-open the banner via
 * openConsentBanner() regardless of region.
 */
export function ConsentBanner() {
  const [open, setOpen] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [analyticsOn, setAnalyticsOn] = useState(false);
  const [advertisingOn, setAdvertisingOn] = useState(false);
  const es = useLocation().pathname.startsWith("/es/");
  const t = (en: string, spanish: string) => es ? spanish : en;

  useEffect(() => {
    if (!isProductionHost()) return;
    if (getConsent()) return;
    // GPC users have already expressed a global "do not sell/share" choice.
    // Honour it: keep analytics denied (default) and suppress the banner.
    if (isGpcSignaled()) return;

    let cancelled = false;
    let timer: number | undefined;
    void isConsentRequiredRegion().then((required) => {
      if (cancelled || !required) return;
      if (getConsent()) return; // race: user clicked re-open meanwhile
      timer = window.setTimeout(() => {
        if (!getConsent() && !isGpcSignaled()) setOpen(true);
      }, 600);
    });
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, []);


  useEffect(() => {
    return onConsentOpenRequest(() => {
      const current = getConsent();
      setAnalyticsOn(!isGpcSignaled() && current?.analytics === "granted");
      setAdvertisingOn(!isGpcSignaled() && current?.advertising === "granted");
      setShowCustomize(true);
      setOpen(true);
    });
  }, []);

  if (!isProductionHost() || !open) return null;

  const acceptAll = () => {
    setConsent("granted", "user", "granted");
    setOpen(false);
    setShowCustomize(false);
  };

  const rejectAll = () => {
    setConsent("denied");
    setOpen(false);
    setShowCustomize(false);
  };

  const savePreferences = () => {
    setConsent(analyticsOn ? "granted" : "denied", "user", advertisingOn ? "granted" : "denied");
    setOpen(false);
    setShowCustomize(false);
  };

  return (
    <div
      role="dialog"
      aria-label={t("Cookie preferences", "Preferencias de cookies")}
      aria-live="polite"
      className="fixed bottom-4 left-4 right-4 sm:right-auto sm:max-w-[420px] z-[100] animate-in fade-in slide-in-from-bottom-4 duration-300"
    >
      <div className="rounded-xl border border-border bg-card/95 backdrop-blur shadow-xl p-5">
        {/* Not a heading element: the banner is a dialog rendered before the
            page's h1, so a real <h2> here breaks the document heading order.
            The dialog is already labelled via aria-label above. */}
        <p className="font-heading font-semibold text-sm text-foreground">
          {t("Cookies, analytics & advertising", "Cookies, analítica y publicidad")}
        </p>
        <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
          {t("We use essential cookies to run AuthorityLayer. Optional analytics helps us understand usage; optional advertising helps measure campaigns and personalize ads. Choose each independently. Read our ", "Usamos cookies esenciales para operar AuthorityLayer. La analítica opcional nos ayuda a entender el uso; la publicidad opcional permite medir campañas y personalizar anuncios. Elige cada categoría por separado. Consulta nuestra ")}
          <Link to={es ? "/legal/cookies?lang=es" : "/legal/cookies"} className="underline hover:text-foreground">
            {t("cookie policy", "política de cookies")}
          </Link>.
          {isGpcSignaled() && <span className="block mt-2">{t("Global Privacy Control is enabled; optional consent stays denied.", "Global Privacy Control está activado; el consentimiento opcional permanece denegado.")}</span>}

        </p>

        {showCustomize && (
          <div className="mt-4 space-y-3 rounded-lg border border-border/60 bg-background/40 p-3">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-xs font-heading font-medium text-foreground">
                  {t("Essential", "Esenciales")}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {t("Required for sign-in, security and core features.", "Necesarias para iniciar sesión, la seguridad y las funciones esenciales.")}
                </div>
              </div>
              <Switch checked disabled aria-label={t("Essential cookies (always on)", "Cookies esenciales (siempre activas)")} />
            </div>
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-xs font-heading font-medium text-foreground">
                  {t("Analytics & attribution", "Analítica y atribución")}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {t("Google Analytics and affiliate attribution.", "Google Analytics y atribución de afiliados.")}
                </div>
              </div>
              <Switch
                checked={analyticsOn}
                disabled={isGpcSignaled()}
                onCheckedChange={setAnalyticsOn}
                aria-label={t("Analytics consent", "Consentimiento de analítica")}
              />
            </div>
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-xs font-heading font-medium text-foreground">{t("Advertising", "Publicidad")}</div>
                <div className="text-[11px] text-muted-foreground">{t("Google Ads campaign measurement, advertising data and personalized ads; IndexPointer advertising tracking.", "Medición de campañas de Google Ads, datos publicitarios y anuncios personalizados; seguimiento publicitario de IndexPointer.")}</div>
              </div>
              <Switch checked={advertisingOn} disabled={isGpcSignaled()} onCheckedChange={setAdvertisingOn} aria-label={t("Advertising consent", "Consentimiento de publicidad")} />
            </div>
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {showCustomize ? (
            <>
              <Button size="sm" variant="signal" onClick={savePreferences}>
                {t("Save preferences", "Guardar preferencias")}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowCustomize(false)}>
                {t("Back", "Volver")}
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="signal" disabled={isGpcSignaled()} onClick={acceptAll}>
                {t("Accept all", "Aceptar todo")}
              </Button>
              <Button size="sm" variant="outline" onClick={rejectAll}>
                {t("Reject", "Rechazar todo")}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowCustomize(true)}
              >
                {t("Customize", "Personalizar")}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
